import { useEffect, useState } from "react";
import { StoredFilterItem } from "../../../entities/Filter/StoredFiltersResponse";
import CompanyService from "../../../services/CompanyService";
import StoredFiltersService from "../../../services/StoredFiltersService";
import { TranslationService } from "../../../services/TranslationService";
import { ToastService } from "../../shared/bootstrap/Toast";
import FloatingPanelService from "../../shared/FloatingPanel";
import {
  buildGroupingFilter,
  findDefaultGrouping,
  GROUPING_REPORT_ID,
  isDefaultGrouping,
  NO_GROUPING,
  parseGroupingFilter
} from "./SavedGroupingUtils";
import StoredGroupingPanel from "./StoredGroupingPanel";

const getCachedGroupings = () =>
  StoredFiltersService.getListByReport({ report: GROUPING_REPORT_ID }).list;

export const useSavedGroupings = (
  ids: number[],
  onGroupingSelected: (ids: number[]) => void,
  applyDefault: boolean
) => {
  const { translate } = TranslationService;
  const userId = parseInt(CompanyService.getUserid());
  const [groupings, setGroupings] =
    useState<StoredFilterItem[]>(getCachedGroupings);
  const [selectedId, setSelectedId] = useState<number>(() =>
    applyDefault
      ? (findDefaultGrouping(groupings, userId)?.StoredFilterID ?? NO_GROUPING)
      : NO_GROUPING
  );
  const [listVersion, setListVersion] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  const selectedGrouping = groupings.find(
    (x) => x.StoredFilterID === selectedId
  );

  // Opens the report with the user's default grouping, only on mount.
  useEffect(() => {
    if (selectedGrouping) {
      onGroupingSelected(parseGroupingFilter(selectedGrouping.Filter));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Stored filters are read from the company cache, which RequestService
  // refreshes after any storedfilter call that returns refreshlists.
  const loadGroupings = (selectId?: number) => {
    setGroupings(getCachedGroupings());
    if (selectId !== undefined) {
      setSelectedId(selectId);
    }
    setListVersion((x) => x + 1);
  };

  const selectGrouping = (id: number) => {
    setSelectedId(id);
    const grouping = groupings.find((x) => x.StoredFilterID === id);
    if (grouping) {
      onGroupingSelected(parseGroupingFilter(grouping.Filter));
    }
  };

  const openPanel = (grouping: Partial<StoredFilterItem>, isNew: boolean) =>
    FloatingPanelService.showPanel({
      title: translate.SaveGrouping,
      children: (
        <StoredGroupingPanel
          grouping={grouping}
          groupings={groupings}
          isNew={isNew}
          onSaved={(id) => loadGroupings(id)}
          onDeleted={() => loadGroupings(NO_GROUPING)}
        />
      ),
      height: 380,
      width: 600,
      position: "center"
    });

  const canManage = CompanyService.canManageSavedGroupings();

  const saveGrouping = async () => {
    if (!canManage || ids.length === 0) {
      return;
    }
    if (!selectedGrouping) {
      openPanel({ Filter: buildGroupingFilter(ids) }, true);
      return;
    }
    setIsSaving(true);
    const result = await StoredFiltersService.update({
      item: {
        StoredFilterID: selectedGrouping.StoredFilterID,
        Filter: buildGroupingFilter(
          ids,
          isDefaultGrouping(selectedGrouping.Filter)
        )
      }
    });
    setIsSaving(false);
    if (result instanceof Error) {
      ToastService.showToast(
        translate.ErrorProcessingRequest,
        undefined,
        "danger"
      );
      return;
    }
    ToastService.showToast(
      translate.DataUpdatedSuccessfully,
      undefined,
      "success"
    );
    loadGroupings(selectedGrouping.StoredFilterID);
  };

  const editGrouping = () => {
    if (canManage && selectedGrouping) {
      openPanel(selectedGrouping, false);
    }
  };

  const items = [
    { Value: translate.Select, Id: NO_GROUPING },
    ...groupings.map((x) => ({ Value: x.Name, Id: x.StoredFilterID! }))
  ];

  return {
    items,
    selectedId,
    listVersion,
    isSaving,
    canManage,
    hasSelection: selectedGrouping !== undefined,
    selectGrouping,
    saveGrouping,
    editGrouping
  };
};
